import { useId } from 'react';
import { faStar as faStarRegular } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faStar as faStarSolid } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';

import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export function StarRating({
  rating = 0,
  className,
  inactiveStarClassName = 'text-slate-300 dark:text-slate-600',
  onChange,
  disabled = false,
  size = 'sm',
}: {
  rating?: number | null;
  className?: string;
  inactiveStarClassName?: string;
  onChange?: (rating: number | null) => void;
  disabled?: boolean;
  size?: 'sm' | 'lg' | 'xl';
}) {
  const normalizedRating = rating || 0;
  const isInteractive = Boolean(onChange);
  const groupName = useId();

  return (
    <div
      className={cn('flex items-center gap-1 text-muted-foreground', className)}
      {...(isInteractive
        ? { role: 'radiogroup', 'aria-label': 'Star Rating' }
        : { role: 'img', 'aria-label': `Rating: ${normalizedRating} out of 5 stars` })}
    >
      {Array.from({ length: 5 }).map((_, index) => {
        const value = index + 1;
        const filled = value <= normalizedRating;
        const star = (
          <Icon
            icon={filled ? faStarSolid : faStarRegular}
            size={size}
            className={filled ? 'text-amber-400' : inactiveStarClassName}
          />
        );

        if (!isInteractive) {
          return (
            <span
              key={value}
              className="inline-flex items-center justify-center"
              aria-hidden="true"
            >
              {star}
            </span>
          );
        }

        return (
          <Label
            key={value}
            className="inline-flex cursor-pointer items-center justify-center transition-colors"
          >
            <Input
              type="radio"
              name={groupName}
              value={value}
              checked={value === normalizedRating}
              disabled={disabled}
              onChange={() => onChange?.(value)}
              // Radios don't fire onChange when the checked option is clicked
              // again, so clearing the rating has to happen on click.
              onClick={() => {
                if (value === normalizedRating) onChange?.(null);
              }}
              className="sr-only"
              aria-label={`${value} star${value === 1 ? '' : 's'}`}
            />
            {star}
          </Label>
        );
      })}
    </div>
  );
}
